import { useEffect, useMemo, useState } from "react";
import { AppModal } from "../../../../../_CORE/APP/Templates/_UI/Modal/_index";
import { AppButton } from "../../../../../_CORE/APP/Templates/_UI/Button/_index";
import { FormGrid, FieldInput, FieldTextarea, FieldSelect, FieldColor, FieldSwitch } from "../../../../../_CORE/APP/Templates/_UI/Form/_index";
import { FaBookOpen, FaInfoCircle, FaLink, FaPalette } from "react-icons/fa";

const CATEGORIES = [
    { value: "GENERAL", label: "General" },
    { value: "LANGUAGE", label: "Lenguaje" },
    { value: "FRAMEWORK", label: "Framework" },
    { value: "BACKEND", label: "Backend" },
    { value: "FRONTEND", label: "Frontend" },
    { value: "FULLSTACK", label: "Full stack" },
    { value: "DATABASE", label: "Base de datos" },
    { value: "API", label: "API / servicios" },
    { value: "DEVOPS", label: "DevOps" },
    { value: "CLOUD", label: "Cloud" },
    { value: "ARCHITECTURE", label: "Arquitectura" },
    { value: "SECURITY", label: "Seguridad" },
    { value: "TESTING", label: "Testing / QA" },
    { value: "AUTOMATION", label: "Automatización" },
    { value: "MOBILE", label: "Mobile" },
    { value: "DESKTOP", label: "Desktop" },
    { value: "LIBRARY", label: "Librería / paquete" },
    { value: "TOOL", label: "Herramienta" },
    { value: "DATA", label: "Datos / analytics" },
    { value: "OTHER", label: "Otro" },
];

const EMPTY = {
    name: "", description: "", technology: "", version: "", category: "GENERAL",
    color: "#e83a52", officialDocsUrl: "", repositoryUrl: "",
    isFavorite: false,
};

const onValues = (manual) => manual ? {
    name: manual.name ?? "",
    description: manual.description ?? "",
    technology: manual.technology ?? "",
    version: manual.version ?? "",
    category: manual.category ?? "GENERAL",
    color: manual.color ?? "#e83a52",
    officialDocsUrl: manual.officialDocsUrl ?? "",
    repositoryUrl: manual.repositoryUrl ?? "",
    isFavorite: !!manual.isFavorite,
} : EMPTY;

export const ManualFormModal = ({ isOpen, onClose, manual = null, onSave, isLoading = false }) => {
    const [useValues, setValues] = useState(EMPTY);

    useEffect(() => {
        if (!isOpen) return;
        setValues(onValues(manual));
    }, [isOpen, manual]);

    const IS_EDIT = !!manual?.id;
    const IS_DIRTY = useMemo(() => JSON.stringify(useValues) !== JSON.stringify(onValues(manual)), [useValues, manual]);

    const onField = (name) => ({
        id: name,
        name,
        value: useValues[name] ?? "",
        onChange: (event) => setValues((prev) => ({ ...prev, [name]: event.target.value })),
    });

    const onSubmit = async () => {
        if (!useValues.name.trim()) return;
        const ok = await onSave?.({
            ...useValues,
            name: useValues.name.trim(),
            technology: useValues.technology.trim() || undefined,
            version: useValues.version.trim() || undefined,
            officialDocsUrl: useValues.officialDocsUrl.trim() || undefined,
            repositoryUrl: useValues.repositoryUrl.trim() || undefined,
        });
        if (ok !== false) onClose?.();
    };

    return (
        <AppModal isOpen={isOpen} onClose={onClose} size="full" isDirty={IS_DIRTY} className="LStudio__manual_form_fullscreen"
            title={IS_EDIT ? "Configurar manual" : "Nuevo manual"}
            description={IS_EDIT
                ? "Define la identidad y organizacion del manual. Esto no modifica el contenido de tus paginas."
                : "Crea el contenedor de tu documentacion. Despues vas a escribir libremente dentro de sus paginas."}
            footer={
                <>
                    <AppButton variant="ghost" tone="neutral" onClick={onClose}>Cancelar</AppButton>
                    <AppButton onClick={onSubmit} isLoading={isLoading}>{IS_EDIT ? "Guardar cambios" : "Crear manual"}</AppButton>
                </>
            }>

            <div className="LStudio__manual_config_layout">
                <div className="LStudio__manual_config_form">
                    <div className="LStudio__app_priority_toggle">
                        <FieldSwitch id="isFavorite" name="isFavorite" label="Favorito" text="Mostrar este manual primero"
                            hint="Destaca este manual para tenerlo más a mano."
                            isChecked={!!useValues.isFavorite}
                            onChange={(e) => setValues((prev) => ({ ...prev, isFavorite: e.target.checked }))} />
                    </div>
                    <div className="LStudio__manual_config_intro">
                        <div className="LStudio__manual_config_intro_icon"><FaInfoCircle /></div>
                        <div>
                            <strong>Que estas configurando</strong>
                            <p>Estos son datos generales del manual: nombre, tecnologia, referencias y organizacion. Tus paginas, comandos, scripts y notas se guardan aparte y no se alteran desde aca.</p>
                        </div>
                    </div>

                    <div className="LStudio__manual_config_section">
                        <div className="LStudio__manual_config_section_title"><FaBookOpen /><span><strong>Identidad</strong><small>Como vas a reconocer este manual en tu biblioteca.</small></span></div>
                        <FormGrid columns={2}>
                            <FieldInput {...onField("name")} label="Nombre del manual" placeholder="Ruby on Rails" isRequired
                                hint="Titulo principal que veras en la biblioteca y en el modal de lectura." />
                            <FieldInput {...onField("technology")} label="Tecnologia principal" placeholder="Ruby on Rails"
                                hint="Stack, lenguaje, framework o herramienta sobre la que trata el manual." />
                            <FieldInput {...onField("version")} label="Version de referencia" placeholder="8.x"
                                hint="Opcional. Util si tus notas dependen de una version concreta." />
                            <FieldSelect {...onField("category")} label="Categoria" options={CATEGORIES}
                                hint="Sirve para clasificar el manual dentro de tu biblioteca personal." />
                            <FieldTextarea {...onField("description")} label="Descripcion" placeholder="Comandos, patrones, troubleshooting y notas que quiero conservar..." rows={4} span={2}
                                hint="Resumen corto del objetivo del manual. No reemplaza el contenido de las paginas." />
                        </FormGrid>
                    </div>

                    <div className="LStudio__manual_config_section">
                        <div className="LStudio__manual_config_section_title"><FaPalette /><span><strong>Apariencia</strong><small>Solo afecta la identificacion visual del manual.</small></span></div>
                        <FormGrid columns={2}>
                            <FieldColor {...onField("color")} label="Color identificador" showSwatches={false}
                                hint="Se usa en el icono/acento del manual. No cambia el tema ni los colores del contenido." />
                            <div className="LStudio__manual_config_color_preview" style={{ "--manual-preview-color": useValues.color || "#e83a52" }}>
                                <span><FaBookOpen /></span>
                                <div><strong>{useValues.name || "Tu manual"}</strong><small>Vista previa del identificador</small></div>
                            </div>
                        </FormGrid>
                    </div>

                    <div className="LStudio__manual_config_section">
                        <div className="LStudio__manual_config_section_title"><FaLink /><span><strong>Referencias</strong><small>Atajos opcionales hacia fuentes externas.</small></span></div>
                        <FormGrid columns={2}>
                            <FieldInput {...onField("officialDocsUrl")} label="Documentacion oficial" placeholder="https://guides.rubyonrails.org"
                                hint="URL de la documentacion oficial para consultarla rapidamente." />
                            <FieldInput {...onField("repositoryUrl")} label="Repositorio / referencia" placeholder="https://github.com/rails/rails"
                                hint="Repositorio, wiki o referencia tecnica relacionada." />
                        </FormGrid>
                    </div>

                </div>

                <aside className="LStudio__manual_config_guide">
                    <span className="LStudio__manual_config_guide_badge"><FaInfoCircle /> Modo guia</span>
                    <h3>Como pensar un manual</h3>
                    <p>El manual es el <strong>contenedor</strong>. Las paginas son el contenido real donde escribis Markdown, comandos, scripts, ejemplos y apuntes.</p>
                    <ol>
                        <li><strong>Nombre:</strong> algo amplio, por ejemplo “Ruby on Rails”.</li>
                        <li><strong>Tecnologia:</strong> el stack principal, por ejemplo “Rails”.</li>
                        <li><strong>Version:</strong> solo si importa para tus notas, por ejemplo “8.x”.</li>
                        <li><strong>Categoria:</strong> ayuda a ordenar tu biblioteca.</li>
                        <li><strong>Referencias:</strong> links externos; no se copian dentro de las paginas.</li>
                    </ol>
                    <div className="LStudio__manual_config_guide_example">
                        <span>Ejemplo</span>
                        <strong>Ruby on Rails</strong>
                        <small>Framework · Rails · 8.x</small>
                        <p>Despues, adentro, podes crear paginas como Active Record, Routing, Testing, Deploy, snippets, errores comunes, etc.</p>
                    </div>
                    <p className="LStudio__manual_config_guide_note"><strong>Importante:</strong> cambiar estos datos nunca borra ni reescribe tus paginas.</p>
                </aside>
            </div>
        </AppModal>
    );
};
